// The partner programme (refer and earn), W47.
//
// A partner is an ordinary account that has enrolled, so every authenticated
// call here is the signed-in organisation's own: there is no partner id to
// pass and no way to read anybody else's. The operator's half of the
// programme lives in `admin.ts`.
//
// Two calls need no token at all: the programme's terms, which the marketing
// site renders, and the code check the sign-up form runs as somebody types.
//
// Money on this surface is rand the platform owes the partner, in minor
// units, and never credits. What a referred company was charged, and what it
// cost the platform, are deliberately absent: an earnings row carries the
// partner's own share and nothing else.

import { http } from './_core/request.js';
import { buildPageQuery, type PageOpts } from './_core/pagination.js';
import type { Envelope, EnvelopeWithMeta, Meta } from './_core/envelope.js';
import type * as gen from './generated/billing/index.js';

export type AffiliateProgramme = gen.AffiliateProgrammeDto;
export type ReferralCodeCheck = gen.ReferralCodeDto;
export type Affiliate = gen.AffiliateDto;
export type AffiliateBalances = gen.AffiliateBalancesDto;
export type AffiliateTerms = gen.AffiliateTermsDto;
export type Referral = gen.AffiliateReferralDto;
export type Earning = gen.AffiliateEarningDto;
export type PayoutAccount = gen.PayoutAccountDto;
export type Withdrawal = gen.WithdrawalDto;
export type ReferredBy = gen.ReferredByDto;

export type EnrolRequest = gen.EnrolAffiliateRequest;
export type UpdateAffiliateRequest = gen.UpdateAffiliateRequest;
export type SavePayoutAccountRequest = gen.SavePayoutAccountRequest;

/** `eft` pays a South African bank account; `wallet_credit` turns earnings into credits at once. */
export type WithdrawalMethod = 'eft' | 'wallet_credit';

/** What a cursor-paged read of this surface reports. */
export interface AffiliatePageMeta extends Meta {
  count: number;
  total: number;
  limit: number;
  next_cursor?: string;
}

/** The earnings list also reports the two balances, so one call fills a page. */
export interface EarningsPageMeta extends AffiliatePageMeta {
  pending_minor: number;
  available_minor: number;
}

// ── Anonymous ───────────────────────────────────────────────────────────────

/** The programme as a visitor sees it: the share, the term, the clearing period and the minimum. */
export function getProgramme(): Promise<Envelope<AffiliateProgramme>> {
  return http.get<Envelope<AffiliateProgramme>>('/v1/public/affiliate-programme', { auth: false });
}

/**
 * Checks a referral code and returns the partner's name, so a sign-up form can
 * say "Referred by Thandi Marketing" before the account exists.
 *
 * 404 `CODE_UNKNOWN` for a code that is not one of ours, or whose partner is
 * not active. Nothing else about a partner is returned.
 */
export function lookupCode(code: string): Promise<Envelope<ReferralCodeCheck>> {
  return http.get<Envelope<ReferralCodeCheck>>(`/v1/public/referral-codes/${encodeURIComponent(code)}`, { auth: false });
}

// ── The signed-in partner ───────────────────────────────────────────────────

/** 404 `NOT_ENROLLED` when this account has not applied to the programme. */
export function me(): Promise<Envelope<Affiliate>> {
  return http.get<Envelope<Affiliate>>('/v1/affiliate/me');
}

/**
 * Applies to the programme. An operator approves before a code is issued
 * unless the programme is set to approve automatically.
 *
 * 409 `ALREADY_ENROLLED`, 400 `TERMS_NOT_ACCEPTED`, 403 `PROGRAMME_DISABLED`.
 */
export function enrol(body: EnrolRequest): Promise<Envelope<Affiliate>> {
  return http.post<Envelope<Affiliate>>('/v1/affiliate/enrol', { body });
}

export function update(body: UpdateAffiliateRequest): Promise<Envelope<Affiliate>> {
  return http.patch<Envelope<Affiliate>>('/v1/affiliate/me', { body });
}

/** The terms as they stand, and the version a partner would be accepting. */
export function terms(): Promise<Envelope<AffiliateTerms>> {
  return http.get<Envelope<AffiliateTerms>>('/v1/affiliate/terms');
}

/** The companies this partner introduced. `status` is one of `earning`, `ended`, `revoked`. */
export function listReferrals(opts?: PageOpts & { status?: string }): Promise<EnvelopeWithMeta<Referral[], AffiliatePageMeta>> {
  return http.get<EnvelopeWithMeta<Referral[], AffiliatePageMeta>>('/v1/affiliate/referrals', {
    query: { ...buildPageQuery(opts), status: opts?.status },
  });
}

/**
 * The partner's own ledger: a day's share, an operator's adjustment, a
 * withdrawal or its reversal. `meta` carries the two balances as well, so one
 * call fills a page.
 */
export function listEarnings(
  opts?: PageOpts & { kind?: string; from?: string; to?: string }
): Promise<EnvelopeWithMeta<Earning[], EarningsPageMeta>> {
  return http.get<EnvelopeWithMeta<Earning[], EarningsPageMeta>>('/v1/affiliate/earnings', {
    query: { ...buildPageQuery(opts), kind: opts?.kind, from: opts?.from, to: opts?.to },
  });
}

export function listWithdrawals(): Promise<Envelope<Withdrawal[]>> {
  return http.get<Envelope<Withdrawal[]>>('/v1/affiliate/withdrawals');
}

/**
 * Asks for a withdrawal. The amount leaves the available balance in the same
 * transaction, so it cannot be asked for twice.
 *
 * 400 `BELOW_MINIMUM`, 400 `PAYOUT_ACCOUNT_REQUIRED`, 403
 * `AFFILIATE_NOT_ACTIVE`, 409 `WITHDRAWAL_OPEN`, 409 `INSUFFICIENT_AVAILABLE`.
 */
export function requestWithdrawal(body: {
  amount_minor: number;
  method?: WithdrawalMethod;
  payout_account_id?: string;
}): Promise<Envelope<Withdrawal>> {
  return http.post<Envelope<Withdrawal>>('/v1/affiliate/withdrawals', { body });
}

/** 409 `WITHDRAWAL_NOT_OPEN` once it has been approved, paid or answered. */
export function cancelWithdrawal(id: string): Promise<Envelope<Withdrawal>> {
  return http.post<Envelope<Withdrawal>>(`/v1/affiliate/withdrawals/${encodeURIComponent(id)}/cancel`);
}

/** The saved bank account, masked to the last four digits. 404 `NO_PAYOUT_ACCOUNT`. */
export function getPayoutAccount(): Promise<Envelope<PayoutAccount>> {
  return http.get<Envelope<PayoutAccount>>('/v1/affiliate/payout-account');
}

/**
 * Saves the bank account withdrawals are paid to. Refused with 409
 * `WITHDRAWAL_OPEN` while a withdrawal is waiting, and the partner is emailed
 * whenever it changes.
 */
export function savePayoutAccount(body: SavePayoutAccountRequest): Promise<Envelope<PayoutAccount>> {
  return http.put<Envelope<PayoutAccount>>('/v1/affiliate/payout-account', { body });
}
